import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { useLogto } from '@logto/react'

import {
  installAccessTokenGetter,
  installClearAccessToken,
} from '@/api/client'
import { E2E_STUB_ACCESS_TOKEN, isE2EAuthStub } from '@/auth/e2e-stub'

const apiResource = import.meta.env.VITE_API_URL

/**
 * Per-call cap on Logto's getAccessToken / clearAccessToken. The Logto JS
 * SDK has no fetch timeout on its internal /oidc/token request, so a
 * stalled refresh after idle/sleep would otherwise stretch authFetch's
 * `acquireToken` await indefinitely (the AbortController guards the
 * BACKEND fetch, not the token-getter). Time-bounding here means a
 * wedged IdP shows up to authFetch as "no token" — which trips the
 * 401 + session-expired path the rest of the auth stack already knows
 * how to handle — instead of dragging every query into a forever-pending
 * state.
 */
export const LOGTO_TOKEN_TIMEOUT_MS = 15_000

async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  return await new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`))
    }, ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err: unknown) => {
        clearTimeout(timer)
        reject(err instanceof Error ? err : new Error(String(err)))
      },
    )
  })
}

/**
 * Stub bridge used by Playwright CI runs. Installs a static getter that
 * returns the e2e bearer so authFetch can attach `Authorization` headers
 * to mocked-out backend calls without touching Logto. Tree-shaken out of
 * production builds when VITE_E2E_AUTH_STUB is unset.
 */
function TokenBridgeStub({ children }: { children: ReactNode }) {
  useEffect(() => {
    installAccessTokenGetter(async () => E2E_STUB_ACCESS_TOKEN)
    installClearAccessToken(async () => {})
  }, [])
  return <>{children}</>
}

/**
 * Bridges the Logto hook's `getAccessToken` and `clearAccessToken` into the
 * module-singleton openapi-fetch client. Logto owns refresh; we just keep
 * stable pointers the fetch wrapper can call without re-rendering.
 *
 * `clearAccessToken` is what makes the post-401 refresh actually work:
 * Logto's getAccessToken serves the locally-cached bearer if it thinks the
 * token is still valid, so when the BACKEND disagrees (clock skew, mobile
 * sleep, server-side revoke) we clear the cache to force a refresh_token
 * grant on the next getAccessToken call.
 *
 * The Logto SDK does not memoize the functions returned from useLogto(),
 * so each render produces fresh references. We capture them in refs and
 * read `.current` inside the installed getters. That lets us install into
 * the module singleton exactly ONCE on mount — listing the Logto functions
 * as effect deps would re-install on every render and is the same latent
 * footgun that caused the /dashboard infinite re-render fixed in #36 for
 * useUser.
 */
function TokenBridgeReal({ children }: { children: ReactNode }) {
  const { getAccessToken, clearAccessToken } = useLogto()
  const accessTokenRef = useRef(getAccessToken)
  const clearAccessTokenRef = useRef(clearAccessToken)

  // Sync the refs to the latest values from useLogto() after every render.
  useEffect(() => {
    accessTokenRef.current = getAccessToken
    clearAccessTokenRef.current = clearAccessToken
  })

  // Install the bridge once; the closures read *.current at call time, so
  // they always see the freshest Logto functions.
  useEffect(() => {
    installAccessTokenGetter(async () => {
      try {
        const token = await withTimeout(
          Promise.resolve(accessTokenRef.current(apiResource)),
          LOGTO_TOKEN_TIMEOUT_MS,
          'Logto getAccessToken',
        )
        return token ?? ''
      } catch (err) {
        // Hung or rejected refresh — surface as "no token" so authFetch's
        // 401 path can route the user to the session-expired UX instead of
        // dragging the calling query into a forever-pending state.
        console.warn('TokenBridge: getAccessToken failed', err)
        return ''
      }
    })
    installClearAccessToken(async () => {
      const clear = clearAccessTokenRef.current
      if (!clear) return
      try {
        await withTimeout(
          Promise.resolve(clear()),
          LOGTO_TOKEN_TIMEOUT_MS,
          'Logto clearAccessToken',
        )
      } catch (err) {
        // clearAccessToken hanging is non-fatal — the refresh path treats
        // it as best-effort. Drop the wait so the caller's refresh attempt
        // can continue (and itself time-bound).
        console.warn('TokenBridge: clearAccessToken failed', err)
      }
    })
  }, [])

  return <>{children}</>
}

// Pick the real-or-stub bridge at module load. We thin-wrap the chosen
// component so eslint's `react-refresh/only-export-components` sees a
// proper named component export instead of a ternary-returned const.
const ChosenBridge = isE2EAuthStub ? TokenBridgeStub : TokenBridgeReal

export function TokenBridge({ children }: { children: ReactNode }) {
  return <ChosenBridge>{children}</ChosenBridge>
}
